import { REPORT } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, countUnique } from '@utils/summaries';
import { Materials } from '@consts/materials';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { getDataByKey, getUniqueDates, groupBy } from '@utils/data';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';

const MaterialPctMap = {
    'Original %': 'originalPct',
    'Global Original %': 'globalOriginalPct',
    'Matte %': 'mattePct',
    'Global Matte %': 'globalMattePct',
    'Fusion %': 'fusionPct',
    'Global Fusion %': 'globalFusionPct',
    'Style Skin %': 'styleSkinPct',
    'Global Style Skin %': 'globalStyleSkinPct',
    'Pruvia %': 'pruviaPct',
    'Global Pruvia %': 'globalPruviaPct',
};

interface MaterialTypeCutPctByMonthProps {
    dateRange: { startDate: string; endDate: string };
}

export const MaterialTypeCutPctByMonth = ({ dateRange }: MaterialTypeCutPctByMonthProps) => {
    const [range, setRange] = useRange('MaterialTypeCutPctByMonth');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.PRODUCTS_OVERALL_MATERIAL_TYPE_CUT_PERCENTAGE_OVER_TIME,
        params: { range, ...dateRange },
    });

    const dateKeys = getUniqueDates(data, range);

    const groupedByDate = groupBy(data, 'date');

    const getData = getDataByKey(dateKeys, groupedByDate);

    return (
        <BaseReport
            setRange={setRange}
            title={'Material Type Cut % by Month'}
            description={
                'This graph shows the % breakdown of each material type by month, allowing you to compare them to the global average.'
            }
            note={'Note: Fusion data includes both Fusion and Fusion for Curved Devices.'}
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            range={range}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: ['Date', 'Material', 'Material %', 'Global Avg'],
                data: data.flatMap((item) => [
                    [item.date, Materials.Original, item.originalPct, item.globalOriginalPct],
                    [item.date, Materials.Matte, item.mattePct, item.globalMattePct],
                    [item.date, Materials.Fusion, item.fusionPct, item.globalFusionPct],
                    [item.date, Materials.StyleSkins, item.styleSkinPct, item.globalStyleSkinPct],
                    [item.date, Materials.Pruvia, item.pruviaPct, item.globalPruviaPct],
                ]),
                summaries: [countUnique, countUnique, average, average],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort, basicSort],
            }}
            chartOptions={chart(
                300,
                [{ name: 'Material Type Cut %', opposite: false }],
                dateKeys,
                Object.entries(MaterialPctMap).map(([name, value]) => ({
                    name,
                    data: getData(value as any),
                })),
                undefined,
                {
                    legend: {
                        align: 'right',
                        verticalAlign: 'top',
                    },
                }
            )}
        />
    );
};
